"use client"

import { useEffect, useRef } from "react"

export default function MapComponent({ drivers, onDriverClick, routeAnimation, initialLocation }) {
  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef({})
  const routeLayerRef = useRef(null)
  const animationRef = useRef(null)
  const routeMarkersRef = useRef([])

  useEffect(() => {
    // Load Leaflet dynamically
    const loadLeaflet = async () => {
      if (typeof window === "undefined") return

      // Load Leaflet CSS
      if (!document.querySelector('link[href*="leaflet"]')) {
        const link = document.createElement("link")
        link.rel = "stylesheet"
        link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
        document.head.appendChild(link)
      }

      // Load Leaflet JS
      if (!window.L) {
        const script = document.createElement("script")
        script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"
        script.onload = initializeMap
        document.head.appendChild(script)
      } else {
        initializeMap()
      }
    }

    const initializeMap = () => {
      if (!mapRef.current || mapInstanceRef.current) return

      const L = window.L

      // Initialize map with initial location
      const initialLat = initialLocation?.lat || 4.6522
      const initialLng = initialLocation?.lng || -74.1059
      const map = L.map(mapRef.current).setView([initialLat, initialLng], 12)
      mapInstanceRef.current = map

      // Add blue-toned dark tile layer
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "© OpenStreetMap contributors",
        maxZoom: 19,
      }).addTo(map)

      // Create simple vehicle icon with status colors
      const createDriverIcon = (available, category) => {
        const color = available ? "#10b981" : "#ef4444" // Verde si disponible, rojo si ocupado

        return L.divIcon({
          html: `
            <div style="
              position: relative;
              width: 50px;
              height: 50px;
              display: flex;
              align-items: center;
              justify-content: center;
              filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
            ">
              <!-- Simple vehicle icon -->
              <div style="
                width: 40px;
                height: 40px;
                background: ${color};
                border: 3px solid white;
                border-radius: 8px;
                box-shadow: 0 4px 12px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 20px;
                color: white;
                font-weight: bold;
                animation: ${available ? "vehicle-pulse 2s infinite" : "none"};
              ">🚗</div>
            </div>
            
            <style>
              @keyframes vehicle-pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.1); }
              }
            </style>
          `,
          className: "simple-vehicle-icon",
          iconSize: [50, 50],
          iconAnchor: [25, 25],
        })
      }

      // Add markers for drivers
      drivers.forEach((driver) => {
        const icon = createDriverIcon(driver.available, driver.category)

        const marker = L.marker([driver.lat, driver.lng], { icon })
          .addTo(map)
          .on("click", () => onDriverClick(driver))

        // Add enhanced popup with detailed driver information
        const popupContent = `
          <div style="min-width: 350px; text-align: center; padding: 15px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); border-radius: 15px; color: white;">
            <div style="
              background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
              color: white;
              padding: 20px;
              border-radius: 12px;
              margin: -15px -15px 20px -15px;
              position: relative;
              overflow: hidden;
            ">
              <div style="
                position: absolute;
                top: -50%;
                left: -50%;
                width: 200%;
                height: 200%;
                background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
                animation: shimmer 3s ease-in-out infinite;
              "></div>
              <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 12px; position: relative; z-index: 2;">
                <div style="
                  width: 20px; 
                  height: 20px; 
                  border-radius: 50%; 
                  background: ${driver.available ? "#10b981" : "#ef4444"};
                  box-shadow: 0 0 15px rgba(255,255,255,0.6);
                  animation: status-pulse 2s infinite;
                "></div>
                <strong style="font-size: 18px; text-shadow: 0 2px 4px rgba(0,0,0,0.3);">${driver.name}</strong>
              </div>
              <div style="font-size: 14px; opacity: 0.95; position: relative; z-index: 2;">
                ${driver.category || 'General'} • ${driver.vehicle}
              </div>
            </div>
            
            <div style="
              background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
              padding: 12px;
              border-radius: 8px;
              margin-bottom: 12px;
              border: 1px solid rgba(255,255,255,0.1);
            ">
              <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">ID Conductor</div>
              <div style="font-size: 14px; font-weight: bold; color: white;">${driver.id}</div>
            </div>
            
            <div style="display: flex; gap: 8px; margin-bottom: 12px;">
              <div style="
                background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
                padding: 10px;
                border-radius: 8px;
                border: 1px solid rgba(255,255,255,0.1);
                flex: 1;
              ">
                <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Rating</div>
                <div style="display: flex; align-items: center; justify-content: center; gap: 4px; color: #fbbf24;">
                  <span style="font-size: 14px;">⭐</span>
                  <span style="font-size: 14px; font-weight: bold;">${driver.rating || '4.5'}</span>
                </div>
              </div>
              
              <div style="
                background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
                padding: 10px;
                border-radius: 8px;
                border: 1px solid rgba(255,255,255,0.1);
                flex: 1;
              ">
                <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Estado</div>
                <div style="font-size: 14px; font-weight: bold; color: ${driver.available ? '#10b981' : '#ef4444'}">
                  ${driver.available ? 'Disponible' : 'Ocupado'}
                </div>
              </div>
            </div>
            
            <div style="
              background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
              padding: 12px;
              border-radius: 8px;
              margin-bottom: 15px;
              border: 1px solid rgba(255,255,255,0.1);
            ">
              <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Última actualización</div>
              <div style="font-size: 14px; font-weight: bold; color: white;">${driver.availableTime || 'N/A'}</div>
            </div>
            
            <div style="
              background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
              padding: 12px 16px;
              border-radius: 8px;
              font-size: 12px;
              color: white;
              font-weight: 600;
              box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
              animation: cta-glow 2s ease-in-out infinite alternate;
            ">
              🎯 Haz clic para ver timeline completo
            </div>
            
            <style>
              @keyframes shimmer {
                0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
                100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
              }
              @keyframes status-pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.2); }
              }
              @keyframes cta-glow {
                0% { box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3); }
                100% { box-shadow: 0 6px 20px rgba(6, 182, 212, 0.5); }
              }
            </style>
          </div>
        `

        marker.bindPopup(popupContent, {
          maxWidth: 350,
          className: "mega-custom-popup",
        })
        markersRef.current[driver.id] = marker
      })
    }

    loadLeaflet()

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
      if (animationRef.current) {
        clearTimeout(animationRef.current)
      }
      // Clean up route layers and markers
      if (routeLayerRef.current && mapInstanceRef.current && mapInstanceRef.current.hasLayer(routeLayerRef.current)) {
        mapInstanceRef.current.removeLayer(routeLayerRef.current)
      }
      routeMarkersRef.current.forEach((marker) => {
        if (mapInstanceRef.current && mapInstanceRef.current.hasLayer(marker)) {
          mapInstanceRef.current.removeLayer(marker)
        }
      })
      routeMarkersRef.current = []
    }
  }, [])

  // Update markers when drivers change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.L) return

    const L = window.L

    // Clear existing markers
    Object.values(markersRef.current).forEach((marker) => {
      mapInstanceRef.current.removeLayer(marker)
    })
    markersRef.current = {}

    // Create simple vehicle icon with status colors (same as above)
    const createDriverIcon = (available, category) => {
      const color = available ? "#10b981" : "#ef4444" // Verde si disponible, rojo si ocupado

      return L.divIcon({
        html: `
          <div style="
            position: relative;
            width: 50px;
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: center;
            filter: drop-shadow(0 4px 8px rgba(0,0,0,0.3));
          ">
            <!-- Simple vehicle icon -->
            <div style="
              width: 40px;
              height: 40px;
              background: ${color};
              border: 3px solid white;
              border-radius: 8px;
              box-shadow: 0 4px 12px rgba(0,0,0,0.3);
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 20px;
              color: white;
              font-weight: bold;
              animation: ${available ? "vehicle-pulse 2s infinite" : "none"};
            ">🚗</div>
          </div>
          
          <style>
            @keyframes vehicle-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.1); }
            }
          </style>
        `,
        className: "simple-vehicle-icon",
        iconSize: [50, 50],
        iconAnchor: [25, 25],
      })
    }

    // Add new markers
    drivers.forEach((driver) => {
      const icon = createDriverIcon(driver.available, driver.category)

      const marker = L.marker([driver.lat, driver.lng], { icon })
        .addTo(mapInstanceRef.current)
        .on("click", () => onDriverClick(driver))

      // Add enhanced popup with detailed driver information (same as above)
      const popupContent = `
        <div style="min-width: 350px; text-align: center; padding: 15px; background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); border-radius: 15px; color: white;">
          <div style="
            background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
            color: white;
            padding: 20px;
            border-radius: 12px;
            margin: -15px -15px 20px -15px;
            position: relative;
            overflow: hidden;
          ">
            <div style="
              position: absolute;
              top: -50%;
              left: -50%;
              width: 200%;
              height: 200%;
              background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
              animation: shimmer 3s ease-in-out infinite;
            "></div>
            <div style="display: flex; align-items: center; gap: 15px; margin-bottom: 12px; position: relative; z-index: 2;">
              <div style="
                width: 20px; 
                height: 20px; 
                border-radius: 50%; 
                background: ${driver.available ? "#10b981" : "#ef4444"};
                box-shadow: 0 0 15px rgba(255,255,255,0.6);
                animation: status-pulse 2s infinite;
              "></div>
              <strong style="font-size: 18px; text-shadow: 0 2px 4px rgba(0,0,0,0.3);">${driver.name}</strong>
            </div>
            <div style="font-size: 14px; opacity: 0.95; position: relative; z-index: 2;">
              ${driver.category || 'General'} • ${driver.vehicle}
            </div>
          </div>
          
          <div style="
            background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 12px;
            border: 1px solid rgba(255,255,255,0.1);
          ">
            <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">ID Conductor</div>
            <div style="font-size: 14px; font-weight: bold; color: white;">${driver.id}</div>
          </div>
          
          <div style="display: flex; gap: 8px; margin-bottom: 12px;">
            <div style="
              background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
              padding: 10px;
              border-radius: 8px;
              border: 1px solid rgba(255,255,255,0.1);
              flex: 1;
            ">
              <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Rating</div>
              <div style="display: flex; align-items: center; justify-content: center; gap: 4px; color: #fbbf24;">
                <span style="font-size: 14px;">⭐</span>
                <span style="font-size: 14px; font-weight: bold;">${driver.rating || '4.5'}</span>
              </div>
            </div>
            
            <div style="
              background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
              padding: 10px;
              border-radius: 8px;
              border: 1px solid rgba(255,255,255,0.1);
              flex: 1;
            ">
              <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Estado</div>
              <div style="font-size: 14px; font-weight: bold; color: ${driver.available ? '#10b981' : '#ef4444'}">
                ${driver.available ? 'Disponible' : 'Ocupado'}
              </div>
            </div>
          </div>
          
          <div style="
            background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
            padding: 12px;
            border-radius: 8px;
            margin-bottom: 15px;
            border: 1px solid rgba(255,255,255,0.1);
          ">
            <div style="font-size: 11px; color: rgba(255,255,255,0.7); margin-bottom: 4px;">Última actualización</div>
            <div style="font-size: 14px; font-weight: bold; color: white;">${driver.availableTime || 'N/A'}</div>
          </div>
          
          <div style="
            background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
            padding: 12px 16px;
            border-radius: 8px;
            font-size: 12px;
            color: white;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3);
            animation: cta-glow 2s ease-in-out infinite alternate;
          ">
            🎯 Haz clic para ver timeline completo
          </div>
          
          <style>
            @keyframes shimmer {
              0% { transform: translateX(-100%) translateY(-100%) rotate(45deg); }
              100% { transform: translateX(100%) translateY(100%) rotate(45deg); }
            }
            @keyframes status-pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.2); }
            }
            @keyframes cta-glow {
              0% { box-shadow: 0 4px 12px rgba(6, 182, 212, 0.3); }
              100% { box-shadow: 0 6px 20px rgba(6, 182, 212, 0.5); }
            }
          </style>
        </div>
      `

      marker.bindPopup(popupContent, {
        maxWidth: 350,
        className: "mega-custom-popup",
      })
      markersRef.current[driver.id] = marker
    })
  }, [drivers])

  // Center map when initial location changes
  useEffect(() => {
    if (mapInstanceRef.current && initialLocation) {
      mapInstanceRef.current.setView([initialLocation.lat, initialLocation.lng], 12)
    }
  }, [initialLocation])

  // Enhanced route animation with blue-green theme
  useEffect(() => {
    if (!routeAnimation || !mapInstanceRef.current || !window.L) return

    const L = window.L
    const map = mapInstanceRef.current

    // Clear previous route and markers
    if (routeLayerRef.current) {
      map.removeLayer(routeLayerRef.current)
    }
    routeMarkersRef.current.forEach((marker) => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker)
      }
    })
    routeMarkersRef.current = []

    // Create route coordinates
    const routeCoords = routeAnimation.steps.map((step) => [step.lat, step.lng])

    // Create animated polyline
    let currentIndex = 0
    const animatedCoords = []

    const animateRoute = () => {
      if (currentIndex < routeCoords.length) {
        animatedCoords.push(routeCoords[currentIndex])

        // Remove previous route layer
        if (routeLayerRef.current) {
          map.removeLayer(routeLayerRef.current)
        }

        // Create enhanced polyline with cyan-blue gradient
        routeLayerRef.current = L.polyline(animatedCoords, {
          color: "#06b6d4", // cyan-500
          weight: 8,
          opacity: 0.9,
          dashArray: "20, 15",
          lineCap: "round",
          lineJoin: "round",
          className: "animated-route",
        }).addTo(map)

        // Add enhanced markers for each step with blue-green theme
        const currentStep = routeAnimation.steps[currentIndex]
        const isCurrentStep = true

        const stepMarker = L.marker([currentStep.lat, currentStep.lng], {
          icon: L.divIcon({
            html: `
              <div style="
                width: 40px;
                height: 40px;
                background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
                border: 4px solid white;
                border-radius: 50%;
                box-shadow: 
                  0 8px 20px rgba(0,0,0,0.4),
                  0 0 30px rgba(6, 182, 212, 0.6);
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                font-size: 14px;
                animation: route-step-bounce 1s infinite;
                position: relative;
              ">
                ${currentIndex + 1}
                <div style="
                  position: absolute;
                  width: 50px;
                  height: 50px;
                  border: 2px solid #06b6d4;
                  border-radius: 50%;
                  animation: route-step-pulse 2s infinite;
                  opacity: 0.5;
                "></div>
              </div>
              <style>
                @keyframes route-step-bounce {
                  0%, 100% { transform: translateY(0) scale(1); }
                  50% { transform: translateY(-5px) scale(1.1); }
                }
                @keyframes route-step-pulse {
                  0% { transform: scale(1); opacity: 0.5; }
                  100% { transform: scale(1.4); opacity: 0; }
                }
              </style>
            `,
            className: "route-step-marker",
            iconSize: [40, 40],
            iconAnchor: [20, 20],
          }),
        }).addTo(map)

        // Enhanced popup for route steps with blue-green theme
        stepMarker.bindPopup(`
          <div style="
            text-align: center; 
            padding: 15px; 
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); 
            border-radius: 12px; 
            color: white;
            min-width: 200px;
          ">
            <div style="
              background: linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%);
              color: white;
              padding: 10px;
              border-radius: 8px;
              margin: -15px -15px 12px -15px;
            ">
              <strong style="font-size: 16px;">Paso ${currentIndex + 1}</strong>
            </div>
            <div style="margin-bottom: 8px;">
              <strong style="color: #fbbf24; font-size: 16px;">${currentStep.time}</strong>
            </div>
            <div style="font-size: 14px; color: rgba(255,255,255,0.9); margin-bottom: 10px;">
              ${currentStep.action}
            </div>
            <div style="
              background: rgba(255,255,255,0.1);
              padding: 8px;
              border-radius: 6px;
              font-size: 11px;
              color: rgba(255,255,255,0.7);
            ">
              📍 ${currentStep.lat.toFixed(4)}, ${currentStep.lng.toFixed(4)}
            </div>
          </div>
        `)

        routeMarkersRef.current.push(stepMarker)

        // Fit map to show the route with padding
        if (animatedCoords.length > 1) {
          map.fitBounds(L.latLngBounds(animatedCoords), { padding: [50, 50] })
        }

        currentIndex++
        animationRef.current = setTimeout(animateRoute, 1200) // Slower animation for better visibility
      } else {
        // Animation complete - highlight final position with emerald-cyan gradient
        const finalStep = routeAnimation.currentStep
        const finalMarker = L.marker([finalStep.lat, finalStep.lng], {
          icon: L.divIcon({
            html: `
              <div style="
                width: 50px;
                height: 50px;
                background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
                border: 5px solid white;
                border-radius: 50%;
                box-shadow: 
                  0 10px 25px rgba(0,0,0,0.5),
                  0 0 40px rgba(16, 185, 129, 0.8);
                animation: final-destination 2s infinite;
                display: flex;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                font-size: 18px;
                position: relative;
              ">🎯
                <div style="
                  position: absolute;
                  width: 70px;
                  height: 70px;
                  border: 3px solid #10b981;
                  border-radius: 50%;
                  animation: final-pulse 2s infinite;
                  opacity: 0.6;
                "></div>
              </div>
              <style>
                @keyframes final-destination {
                  0%, 100% { transform: translateY(0) scale(1) rotate(0deg); }
                  25% { transform: translateY(-8px) scale(1.1) rotate(5deg); }
                  75% { transform: translateY(-4px) scale(1.05) rotate(-5deg); }
                }
                @keyframes final-pulse {
                  0% { transform: scale(1); opacity: 0.6; }
                  100% { transform: scale(1.5); opacity: 0; }
                }
              </style>
            `,
            className: "final-destination-marker",
            iconSize: [50, 50],
            iconAnchor: [25, 25],
          }),
        }).addTo(map)

        finalMarker
          .bindPopup(`
          <div style="
            text-align: center; 
            padding: 20px; 
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%); 
            border-radius: 15px; 
            color: white;
            min-width: 250px;
          ">
            <div style="
              background: linear-gradient(135deg, #10b981 0%, #06b6d4 100%);
              color: white;
              padding: 15px;
              border-radius: 12px;
              margin: -20px -20px 15px -20px;
            ">
              <strong style="font-size: 18px;">🎯 Destino Final</strong>
            </div>
            <div style="margin: 15px 0; padding: 12px; background: rgba(255,255,255,0.1); border-radius: 8px;">
              <div style="font-size: 16px; font-weight: bold; color: #fbbf24; margin-bottom: 5px;">${finalStep.time}</div>
              <div style="font-size: 14px; color: rgba(255,255,255,0.9);">${finalStep.action}</div>
            </div>
            <div style="
              background: rgba(255,255,255,0.1);
              padding: 10px;
              border-radius: 8px;
              font-size: 12px;
              color: rgba(255,255,255,0.7);
            ">
              📍 ${typeof finalStep.lat === 'number' ? finalStep.lat.toFixed(4) : '-'}, ${typeof finalStep.lng === 'number' ? finalStep.lng.toFixed(4) : '-'}
            </div>
          </div>
        `)
          .openPopup()

        routeMarkersRef.current.push(finalMarker)

        // Remove final marker after 5 seconds
        setTimeout(() => {
          if (map.hasLayer(finalMarker)) {
            map.removeLayer(finalMarker)
          }
        }, 5000)
      }
    }

    animateRoute()

    return () => {
      if (animationRef.current) {
        clearTimeout(animationRef.current)
      }
      if (routeLayerRef.current && mapInstanceRef.current && mapInstanceRef.current.hasLayer(routeLayerRef.current)) {
        mapInstanceRef.current.removeLayer(routeLayerRef.current)
      }
      routeMarkersRef.current.forEach((marker) => {
        if (mapInstanceRef.current && mapInstanceRef.current.hasLayer(marker)) {
          mapInstanceRef.current.removeLayer(marker)
        }
      })
      routeMarkersRef.current = []
    }
  }, [routeAnimation])

  return <div ref={mapRef} className="w-full h-full rounded-lg shadow-2xl" style={{ minHeight: "100vh" }} />
}
